import { Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { ON_SURFACE_MUTED } from "../../../components/UserInfo";

export interface ResultCountProps {
  /** The whole result set's size, from the payload's `totalCount` — never `users.length`. */
  totalCount: number;
  /** How many rows are on the current page, for the filtered variant. */
  pageCount: number;
  /** Whether a filter term is applied. The UI knows this; the payload does not. */
  isFiltered: boolean;
}

/**
 * The result-count line above the list (FR-011).
 *
 * **It MUST indicate when the number reflects an active filter** rather than the whole account.
 * `totalCount` alone cannot express that — a bare "12 users" is indistinguishable from an account
 * that has twelve users — so the component selects between **two label keys** based on whether a
 * term is applied. The payload is deliberately **not** asked to describe its own query
 * (data-model.md §3).
 *
 * Reads `totalCount`, not `users.length`: the read is server-paged, so the payload's total describes
 * the whole result set while the array describes one page of at most 50. Using the array length
 * would report "50 users" for a 312-user account.
 */
export const ResultCount = ({
  totalCount,
  pageCount,
  isFiltered
}: ResultCountProps) => (
  <Typography
    /*
     * The prototype's `.um-list-meta{font-size:13px;color:on-surface-muted;padding:0 0 space-2}` —
     * muted secondary text with an 8px gap to the rows below, not another full-strength line.
     */
    color={ON_SURFACE_MUTED}
    data-testid={"user-list-result-count"}
    sx={{ flexShrink: 0, paddingBottom: "8px" }}
    tag={"p"}
    variant={"body-sm"}
  >
    {isFiltered
      ? String(
          Locale.getText("UM_RESULT_COUNT_FILTERED", {
            count: pageCount,
            total: totalCount
          })
        )
      : String(Locale.getText("UM_RESULT_COUNT", { total: totalCount }))}
  </Typography>
);
